/* one frame: its `event:` name, 'message' when it has none, and its `data:` lines joined */
export interface SseFrame {
  event: string
  data: string
}

export interface SseParser {
  feed(chunk: string): void
}

/**
 * A minimal server-sent events parser for fetch streams. Chunks may split
 * anywhere, even inside a line. A frame ends at a blank line and is reported as
 * { event, data } with its `data:` lines joined by '\n'.
 * Example: feed('event: text\ndata: {"delta":"Hi"}\n\n') → onEvent({ event: 'text', data: '{"delta":"Hi"}' }).
 */
export function createSseParser({
  onEvent
}: {
  onEvent: (frame: SseFrame) => void
}): SseParser {
  let buffer = ''
  let event = ''
  let data: string[] = []

  const dispatch = (): void => {
    if (data.length)
      onEvent({ event: event || 'message', data: data.join('\n') })
    event = ''
    data = []
  }

  const processLine = (line: string): void => {
    if (line === '') {
      dispatch()
      return
    }
    /* a comment, used as keep-alive */
    if (line.startsWith(':')) return
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '')
    if (field === 'event') event = value
    if (field === 'data') data.push(value)
  }

  return {
    feed(chunk: string) {
      buffer += chunk
      /* the last element is an unfinished line until the next chunk ends it; a trailing \r may be half of \r\n */
      const lines = buffer.split(/\r\n|\r(?!$)|\n/)
      buffer = lines.pop() ?? ''
      lines.forEach(processLine)
    }
  }
}
